import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Percent, DollarSign, Plus, Trash2, Check, X, Users, Globe, Tag } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdvertiserPricingManager() {
  const [pricingList, setPricingList] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  // Form state
  const [selectedAdvertiser, setSelectedAdvertiser] = useState("");
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [discountType, setDiscountType] = useState("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [description, setDescription] = useState("");
  const [allSitesMode, setAllSitesMode] = useState(true);
  const [allOffersMode, setAllOffersMode] = useState(true);
  const [availableOffers, setAvailableOffers] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [pricingRes, advRes, sitesRes] = await Promise.all([
        axios.get(`${API}/admin/advertiser-pricing`),
        axios.get(`${API}/admin/advertisers`),
        axios.get(`${API}/sites`),
      ]);
      setPricingList(pricingRes.data);
      setAdvertisers(advRes.data);
      setSites(sitesRes.data);
    } catch (err) {
      toast.error("Blad ladowania danych");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Pobierz oferty dla wybranych stron
  useEffect(() => {
    const loadOffers = async () => {
      if (selectedSites.length === 0 && !allSitesMode) {
        setAvailableOffers([]);
        return;
      }
      
      const siteIds = allSitesMode ? sites.map(s => s.id) : selectedSites;
      const allOffers = new Set();
      
      for (const site of sites.filter(s => siteIds.includes(s.id))) {
        if (site.pricing?.pricing) {
          site.pricing.pricing.forEach(p => {
            if (p.name && (p.price > 0 || p.price_pln > 0)) {
              allOffers.add(p.name);
            }
          });
        }
      }
      
      setAvailableOffers(Array.from(allOffers));
    };
    
    loadOffers();
  }, [selectedSites, allSitesMode, sites]);

  const handleSubmit = async () => {
    if (!selectedAdvertiser) {
      toast.error("Wybierz reklamodawce");
      return;
    }
    if (!discountValue || parseFloat(discountValue) <= 0) {
      toast.error("Podaj wartosc rabatu");
      return;
    }

    try {
      await axios.post(`${API}/admin/advertiser-pricing`, {
        advertiser_id: selectedAdvertiser,
        site_ids: allSitesMode ? [] : selectedSites,
        offer_names: allOffersMode ? [] : selectedOffers,
        discount_type: discountType,
        discount_value: parseFloat(discountValue),
        description: description,
      });
      
      toast.success("Dodano indywidualna cene");
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad zapisu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Usunac te cene indywidualna?")) return;
    
    try {
      await axios.delete(`${API}/admin/advertiser-pricing/${id}`);
      toast.success("Usunieto");
      fetchData();
    } catch (err) {
      toast.error("Blad usuwania");
    }
  };

  const handleToggleActive = async (pricing) => {
    try {
      await axios.patch(`${API}/admin/advertiser-pricing/${pricing.id}`, {
        is_active: !pricing.is_active
      });
      toast.success(pricing.is_active ? "Dezaktywowano" : "Aktywowano");
      fetchData();
    } catch (err) {
      toast.error("Blad aktualizacji");
    }
  };

  const resetForm = () => {
    setSelectedAdvertiser("");
    setSelectedSites([]);
    setSelectedOffers([]);
    setDiscountType("percent");
    setDiscountValue("");
    setDescription("");
    setAllSitesMode(true);
    setAllOffersMode(true);
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Ladowanie...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Indywidualne ceny reklamodawcow</h3>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5">
              <Plus className="w-4 h-4" /> Dodaj rabat
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Dodaj indywidualna cene</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Wybor reklamodawcy */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" /> Reklamodawca
                </Label>
                <Select value={selectedAdvertiser} onValueChange={setSelectedAdvertiser}>
                  <SelectTrigger>
                    <SelectValue placeholder="Wybierz reklamodawce..." />
                  </SelectTrigger>
                  <SelectContent>
                    {advertisers.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.company_name || a.display_name || a.identifier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Wybor stron */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Strony
                </Label>
                <div className="flex items-center gap-2 mb-2">
                  <Checkbox 
                    id="allSites" 
                    checked={allSitesMode} 
                    onCheckedChange={setAllSitesMode}
                  />
                  <label htmlFor="allSites" className="text-sm">Wszystkie strony</label>
                </div>
                {!allSitesMode && (
                  <ScrollArea className="h-40 border rounded-md p-2">
                    {sites.map(site => (
                      <div key={site.id} className="flex items-center gap-2 py-1">
                        <Checkbox
                          id={`site-${site.id}`}
                          checked={selectedSites.includes(site.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSites([...selectedSites, site.id]);
                            } else {
                              setSelectedSites(selectedSites.filter(id => id !== site.id));
                            }
                          }}
                        />
                        <label htmlFor={`site-${site.id}`} className="text-sm">{site.name}</label>
                      </div>
                    ))}
                  </ScrollArea>
                )}
              </div>

              {/* Wybor ofert */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Tag className="w-4 h-4" /> Oferty
                </Label>
                <div className="flex items-center gap-2 mb-2">
                  <Checkbox 
                    id="allOffers" 
                    checked={allOffersMode} 
                    onCheckedChange={setAllOffersMode}
                  />
                  <label htmlFor="allOffers" className="text-sm">Wszystkie oferty</label>
                </div>
                {!allOffersMode && availableOffers.length > 0 && (
                  <ScrollArea className="h-40 border rounded-md p-2">
                    {availableOffers.map(offer => (
                      <div key={offer} className="flex items-center gap-2 py-1">
                        <Checkbox
                          id={`offer-${offer}`}
                          checked={selectedOffers.includes(offer)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedOffers([...selectedOffers, offer]);
                            } else {
                              setSelectedOffers(selectedOffers.filter(o => o !== offer));
                            }
                          }}
                        />
                        <label htmlFor={`offer-${offer}`} className="text-sm">{offer}</label>
                      </div>
                    ))}
                  </ScrollArea>
                )}
              </div>

              {/* Typ rabatu */}
              <div className="space-y-2">
                <Label>Typ rabatu</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={discountType === "percent" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDiscountType("percent")}
                    className="gap-1"
                  >
                    <Percent className="w-4 h-4" /> Procent
                  </Button>
                  <Button
                    type="button"
                    variant={discountType === "fixed" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDiscountType("fixed")}
                    className="gap-1"
                  >
                    <DollarSign className="w-4 h-4" /> Stala cena
                  </Button>
                </div>
              </div>

              {/* Wartosc */}
              <div className="space-y-2">
                <Label>
                  {discountType === "percent" ? "Rabat (%)" : "Stala cena (PLN)"}
                </Label>
                <Input
                  type="number"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountType === "percent" ? "np. 15" : "np. 25"}
                  min="0"
                  step={discountType === "percent" ? "1" : "0.01"}
                />
                {discountType === "percent" && (
                  <p className="text-xs text-muted-foreground">
                    Np. 15 = 15% rabatu od ceny podstawowej
                  </p>
                )}
                {discountType === "fixed" && (
                  <p className="text-xs text-muted-foreground">
                    Stala cena dla wszystkich wybranych ofert (niezaleznie od ceny bazowej)
                  </p>
                )}
              </div>

              {/* Opis */}
              <div className="space-y-2">
                <Label>Opis (opcjonalnie)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Np. Rabat dla stalego klienta"
                  rows={2}
                />
              </div>

              <Button onClick={handleSubmit} className="w-full">
                Zapisz indywidualna cene
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Lista istniejacych cen */}
      {pricingList.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Brak indywidualnych cen. Kliknij "Dodaj rabat" aby utworzyc.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pricingList.map(pricing => (
            <Card key={pricing.id} className={!pricing.is_active ? "opacity-60" : ""}>
              <CardContent className="py-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{pricing.advertiser_name}</span>
                      <Badge variant={pricing.is_active ? "default" : "secondary"}>
                        {pricing.is_active ? "Aktywny" : "Nieaktywny"}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {pricing.discount_type === "percent" 
                        ? `Rabat: ${pricing.discount_value}%`
                        : `Stala cena: ${pricing.discount_value} PLN`
                      }
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Strony: {pricing.site_ids?.length > 0 ? pricing.site_ids.length : "Wszystkie"} | 
                      Oferty: {pricing.offer_names?.length > 0 ? pricing.offer_names.join(", ") : "Wszystkie"}
                    </div>
                    {pricing.description && (
                      <div className="text-xs italic">{pricing.description}</div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(pricing)}
                    >
                      {pricing.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(pricing.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
